import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CloseIcon from "images/cancel.svg";

import "./style.scss";

const DictionaryDetail = ({
  isOpenProp,
  onClose,
  onSave,
  bulkUploadMode = false,
  setBulkUploadMode,
  selectedDictionary = null,
}) => {
  const [dictionaries, setDictionaries] = useState([
    {
      name: "",
      type: "CUSTOMER",
      graphic: "",
    },
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm();

  // Initialize dictionaries when selectedDictionary changes
  useEffect(() => {
    if (selectedDictionary) {
      // Edit mode: populate with selected dictionary data
      setDictionaries([
        {
          name: selectedDictionary.name || "",
          type: selectedDictionary.type || "CUSTOMER",
          graphic: selectedDictionary.graphic || "",
          id: selectedDictionary.id, // Keep the ID for updates
        },
      ]);
      setBulkUploadMode(false); // Force single edit mode
    } else {
      // Create mode: reset to empty dictionary
      setDictionaries([
        {
          name: "",
          type: "CUSTOMER",
          graphic: "",
        },
      ]);
    }
  }, [selectedDictionary, setBulkUploadMode]);

  const addDictionary = () => {
    setDictionaries([
      ...dictionaries,
      {
        name: "",
        type: "CUSTOMER",
        graphic: "",
      },
    ]);
  };

  const removeDictionary = index => {
    if (dictionaries.length > 1) {
      const newDictionaries = dictionaries.filter((_, i) => i !== index);
      setDictionaries(newDictionaries);
    }
  };

  const updateDictionary = (index, field, value) => {
    const newDictionaries = [...dictionaries];
    newDictionaries[index][field] = value;
    setDictionaries(newDictionaries);
  };

  const handleFileUpload = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = e => {
          // Strip the data URL prefix to get only the base64 string
          const result = e.target.result;
          if (typeof result === "string") {
            const base64String = result.split(",")[1];
            updateDictionary(index, "graphic", base64String);
          }
        };
        reader.readAsDataURL(file);
      } else {
        toast.error("Please select a valid image file");
      }
    }
  };

  const validateDictionaries = () => {
    const errors = [];

    dictionaries.forEach((dict, index) => {
      // Add null checks to prevent TypeError
      if (!dict) {
        errors.push(`Dictionary ${index + 1}: Invalid dictionary data`);
        return;
      }

      if (!dict.name || !dict.name.trim()) {
        errors.push(`Dictionary ${index + 1}: Name is required`);
      }
      if (!dict.type) {
        errors.push(`Dictionary ${index + 1}: Type is required`);
      }
    });

    // Check for duplicates
    const seen = new Set();
    dictionaries.forEach((dict, index) => {
      // Add null checks here too
      if (!dict || !dict.name || !dict.type) {
        return; // Skip invalid entries
      }

      const key = `${dict.name.trim()}-${dict.type}`;
      if (seen.has(key)) {
        errors.push(
          `Dictionary ${index + 1}: Duplicate name-type combination found`
        );
      }
      seen.add(key);
    });

    return errors;
  };

  const onSubmit = () => {
    const validationErrors = validateDictionaries();

    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    const validDictionaries = dictionaries
      .filter(dict => dict && dict.name && dict.name.trim() && dict.type)
      .map(dict => ({
        ...dict,
        name: dict.name.trim(),
      }));

    if (validDictionaries.length === 0) {
      toast.error("Insert at least one valid dictionary");
      return;
    }

    onSave(validDictionaries);
    handleClose();
  };

  const handleClose = () => {
    setDictionaries([
      {
        name: "",
        type: "CUSTOMER",
        graphic: "",
      },
    ]);
    reset();
    onClose();
  };

  if (!isOpenProp) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content dictionary-detail-modal">
        <div className="modal-header">
          <h2>
            {selectedDictionary
              ? "Edit Dictionary"
              : bulkUploadMode
              ? "Bulk Upload Dictionaries"
              : "New Dictionary"}
          </h2>
          <button className="close-button" onClick={handleClose}>
            <img src={CloseIcon} alt="close" />
          </button>
        </div>

        <div className="modal-body">
          <div className="bulk-mode-toggle">
            <label>
              <input
                type="checkbox"
                checked={bulkUploadMode}
                onChange={e => setBulkUploadMode(e.target.checked)}
                disabled={selectedDictionary !== null} // Disable when editing existing dictionary
              />
              Bulk Upload Mode
              {selectedDictionary && (
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "12px",
                    color: "#6c757d",
                  }}>
                  (Disabled in edit mode)
                </span>
              )}
            </label>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {dictionaries.map((dictionary, index) => (
              <div key={index} className="dictionary-form-group">
                <div className="dictionary-form-header">
                  <h4>Dictionary {index + 1}</h4>
                  {dictionaries.length > 1 && (
                    <button
                      type="button"
                      className="remove-dictionary-btn"
                      onClick={() => removeDictionary(index)}>
                      Remove
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`name-${index}`}>Name *</label>
                    <input
                      id={`name-${index}`}
                      type="text"
                      value={dictionary.name}
                      onChange={e =>
                        updateDictionary(index, "name", e.target.value)
                      }
                      placeholder="Enter dictionary name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`type-${index}`}>Type *</label>
                    <select
                      id={`type-${index}`}
                      value={dictionary.type}
                      onChange={e =>
                        updateDictionary(index, "type", e.target.value)
                      }
                      required>
                      <option value="CUSTOMER">CUSTOMER</option>
                      <option value="TECHNOLOGY">TECHNOLOGY</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor={`graphic-${index}`}>Logo</label>
                  <div className="logo-upload-container">
                    <input
                      id={`graphic-${index}`}
                      type="file"
                      accept="image/*"
                      onChange={e => handleFileUpload(e, index)}
                      className="file-input"
                    />
                    {dictionary.graphic && (
                      <div className="logo-preview">
                        <img
                          src={`data:image/png;base64,${dictionary.graphic}`}
                          alt="Logo preview"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {bulkUploadMode && (
              <div className="add-dictionary-container">
                <button
                  type="button"
                  className="add-dictionary-btn"
                  onClick={addDictionary}>
                  + Add Dictionary
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onSubmit}>
            {selectedDictionary
              ? "Update Dictionary"
              : bulkUploadMode
              ? `Save ${dictionaries.length} Dictionary/ies`
              : "Save Dictionary"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DictionaryDetail;
